/**
 * Shizai Notifications Settings — admin-only page at
 * `/settings/shizai-notifications`.
 *
 * Backing config: `shizai_notification_config` singleton (see backend
 * routers/shizai_notifications.py). Three recipient sources merged at
 * dispatch time:
 *   1. Fixed recipients (this page, textarea)
 *   2. Per-department overrides (this page, expandable table)
 *   3. Role fallback (this page, toggle) — auto-includes all admins +
 *      users with `capability_overrides.shizai_edit = true`
 *
 * Two events wired: REQ create (immediate email) and PO 送信 (queued
 * into the 08:00 JST daily digest — one email covers everything sent
 * in the last 24h, with 一括発注 groups folded into 1 row).
 */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BellRinging, EnvelopeSimple, Buildings, ShieldCheck, Timer, TestTube,
  Plus, Trash, Warning, Crown, UserCircle, Users, ArrowClockwise, CaretDown, CaretRight, XCircle,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/shizai/notifications`;
const DEPTS_API = `${process.env.REACT_APP_BACKEND_URL}/api/mfg/departments`;

export default function ShizaiNotificationsSettings() {
  const [config, setConfig] = useState(null);
  const [depts, setDepts] = useState([]);
  const [roleUsers, setRoleUsers] = useState([]);   // available admin + shizai_edit users
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  // Recipient preview — dry-runs the resolver against the CURRENT (possibly
  // unsaved) config so admins see exactly how many people would get a
  // notification before pressing 保存. Debounced 400ms per edit.
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const previewSeq = useRef(0);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/config`).then((r) => r.data),
      axios.get(DEPTS_API).then((r) => r.data).catch(() => []),
      axios.get(`${API}/available-role-users`).then((r) => r.data).catch(() => []),
    ]).then(([cfg, ds, us]) => {
      setConfig(cfg);
      setDepts(Array.isArray(ds) ? ds : []);
      setRoleUsers(Array.isArray(us) ? us : []);
    }).catch((e) => {
      toast.error(e?.response?.data?.detail || '設定の取得に失敗しました');
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Build the same payload the save button submits — so preview and save
  // stay in lock-step. Memo it so we only refetch preview when something
  // that actually affects recipients changes (not every keystroke on
  // unrelated fields like digest_hour).
  const previewPayload = useMemo(() => {
    if (!config) return null;
    return {
      fixed_recipients: (config.fixed_recipients || []).filter((e) => e && e.includes('@')),
      department_overrides: (config.department_overrides || [])
        .filter((o) => o.department_id)
        .map((o) => ({
          department_id: o.department_id,
          recipients: (o.recipients || []).filter((e) => e && e.includes('@')),
        })),
      role_fallback: !!config.role_fallback,
      role_recipient_user_ids: config.role_recipient_user_ids || [],
      // Backend requires these even though preview only reads recipient
      // fields — supply them so pydantic validation passes.
      events: config.events || { req_create: { enabled: true }, po_sent: { enabled: true } },
      suppress_approval_pending: !!config.suppress_approval_pending,
      suppress_batch_blast: !!config.suppress_batch_blast,
      digest_hour: Number(config.digest_hour) || 8,
    };
  }, [config]);

  // Debounced preview fetch. Cancels the in-flight request when the payload
  // changes so the UI only ever reflects the LATEST edit (avoids flicker /
  // out-of-order responses).
  useEffect(() => {
    if (!previewPayload) return;
    const mySeq = ++previewSeq.current;
    setPreviewLoading(true);
    const timer = setTimeout(async () => {
      try {
        const r = await axios.post(`${API}/preview-recipients`, previewPayload);
        // Ignore stale responses (user typed more since we fired)
        if (mySeq !== previewSeq.current) return;
        setPreview(r.data);
      } catch (_) {
        // Silent — preview failure shouldn't scare admins mid-edit
      } finally {
        if (mySeq === previewSeq.current) setPreviewLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [previewPayload]);

  const save = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const payload = {
        fixed_recipients: (config.fixed_recipients || []).filter((e) => e && e.includes('@')),
        department_overrides: (config.department_overrides || [])
          .filter((o) => o.department_id && (o.recipients || []).some((e) => e && e.includes('@'))),
        role_fallback: !!config.role_fallback,
        role_recipient_user_ids: config.role_recipient_user_ids || [],
        events: config.events || { req_create: { enabled: true }, po_sent: { enabled: true } },
        suppress_approval_pending: !!config.suppress_approval_pending,
        suppress_batch_blast: !!config.suppress_batch_blast,
        digest_hour: Number(config.digest_hour) || 8,
      };
      const fresh = await axios.put(`${API}/config`, payload).then((r) => r.data);
      setConfig(fresh);
      toast.success('通知設定を保存しました');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const runDigestNow = async () => {
    if (!window.confirm('現在キューに入っている発注をまとめてダイジェストメールを送信します。よろしいですか？')) return;
    try {
      await axios.post(`${API}/digest/run-now`);
      toast.success('ダイジェストメールを送信しました');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '送信に失敗しました');
    }
  };

  if (loading || !config) {
    return <div className="p-8 text-sm text-zinc-500">読込中...</div>;
  }

  return (
    <div className="space-y-5 page-enter" data-testid="settings-shizai-notifications">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
            <BellRinging size={26} weight="duotone" className="text-sky-500" />
            資材 通知設定
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            発注依頼作成時のリアルタイム通知と、発注送信の毎朝08:00ダイジェストの宛先を管理します。
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setTestOpen(true)} data-testid="notify-test-btn">
            <TestTube size={14} className="mr-1.5" />
            テスト送信
          </Button>
          <Button variant="outline" onClick={runDigestNow} data-testid="notify-digest-run-btn">
            <Timer size={14} className="mr-1.5" />
            ダイジェスト即時送信
          </Button>
          <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="notify-save-btn">
            {saving ? '保存中...' : '保存'}
          </Button>
        </div>
      </div>

      {/* Recipient preview — live dry-run before 保存 */}
      <RecipientPreviewBanner
        preview={preview}
        loading={previewLoading}
        expanded={previewExpanded}
        onToggle={() => setPreviewExpanded((v) => !v)}
      />

      {/* Fixed recipients */}
      <SectionCard
        title="共通宛先"
        subtitle="全てのイベントに対して必ず送信されます (Slack/メーリングリスト等の代表アドレスを推奨)"
        Icon={EnvelopeSimple}
        accent="from-sky-400 to-blue-500"
      >
        <EmailListEditor
          value={config.fixed_recipients || []}
          onChange={(v) => setConfig((p) => ({ ...p, fixed_recipients: v }))}
          testId="notify-fixed"
        />
      </SectionCard>

      {/* Dept overrides */}
      <SectionCard
        title="部門別 追加宛先"
        subtitle="部門ごとに追加送信先を設定。共通宛先＋部門宛先＋役割fallbackが全てmergeされます。"
        Icon={Buildings}
        accent="from-violet-400 to-purple-500"
      >
        <DeptOverrideEditor
          depts={depts}
          value={config.department_overrides || []}
          onChange={(v) => setConfig((p) => ({ ...p, department_overrides: v }))}
        />
      </SectionCard>

      {/* Role fallback */}
      <SectionCard
        title="役割ベースの追加送信"
        subtitle="有効時: adminロール or shizai_edit権限を持つユーザーが自動で対象に。下でチェックすると「特定のユーザーのみ」に絞れます (CEO等をスパムから除外可)"
        Icon={ShieldCheck}
        accent="from-emerald-400 to-teal-500"
      >
        <div className="flex items-center gap-3 mb-3">
          <Switch
            checked={!!config.role_fallback}
            onCheckedChange={(v) => setConfig((p) => ({ ...p, role_fallback: v }))}
            data-testid="notify-role-fallback"
          />
          <span className="text-sm text-zinc-700">
            {config.role_fallback ? '有効: 下記の対象ユーザーに送信' : '無効: 共通宛先と部門別宛先のみに送信'}
          </span>
        </div>
        {config.role_fallback && (
          <RoleUserPicker
            users={roleUsers}
            value={config.role_recipient_user_ids || []}
            onChange={(v) => setConfig((p) => ({ ...p, role_recipient_user_ids: v }))}
          />
        )}
      </SectionCard>

      {/* Event toggles */}
      <SectionCard
        title="イベント別 ON/OFF"
        subtitle="どのタイミングでメールを送るかを個別に制御"
        Icon={BellRinging}
        accent="from-amber-400 to-orange-500"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <EventToggle
            label="新規発注依頼の作成時"
            desc="WO自動生成 / 補充提案 / 部品受注入力 / バイヤー直接作成 すべての依頼に対して 即時メール送信"
            checked={!!(config.events?.req_create?.enabled)}
            onCheckedChange={(v) => setConfig((p) => ({
              ...p, events: { ...(p.events || {}), req_create: { enabled: v } },
            }))}
            mode="即時"
            testId="notify-event-req-create"
          />
          <EventToggle
            label="発注の送信完了時"
            desc="発注が承認済み → 送信完了 の状態になった時に毎朝08:00 JSTのダイジェストに追加"
            checked={!!(config.events?.po_sent?.enabled)}
            onCheckedChange={(v) => setConfig((p) => ({
              ...p, events: { ...(p.events || {}), po_sent: { enabled: v } },
            }))}
            mode="ダイジェスト"
            testId="notify-event-po-sent"
          />
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 border-t border-zinc-200 pt-4">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!config.suppress_approval_pending}
              onChange={(e) => setConfig((p) => ({ ...p, suppress_approval_pending: e.target.checked }))}
              className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-sky-600"
              data-testid="notify-suppress-approval"
            />
            <div className="text-xs">
              <div className="font-semibold text-zinc-700">承認待ちを除外</div>
              <div className="text-zinc-500 text-[11px]">承認済みの発注のみをダイジェストに含める (推奨)</div>
            </div>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!config.suppress_batch_blast}
              onChange={(e) => setConfig((p) => ({ ...p, suppress_batch_blast: e.target.checked }))}
              className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-sky-600"
              data-testid="notify-suppress-batch"
            />
            <div className="text-xs">
              <div className="font-semibold text-zinc-700">一括発注をまとめる</div>
              <div className="text-zinc-500 text-[11px]">5社×一括発注 → 5行の代わりに1グループ行として表示 (推奨)</div>
            </div>
          </label>
        </div>
        <div className="mt-4 border-t border-zinc-200 pt-4">
          <Label className="text-xs font-semibold text-zinc-600">ダイジェスト送信時刻 (JST)</Label>
          <div className="mt-1 flex items-center gap-2">
            <Select
              value={String(config.digest_hour ?? 8)}
              onValueChange={(v) => setConfig((p) => ({ ...p, digest_hour: Number(v) }))}
            >
              <SelectTrigger className="h-9 w-28" data-testid="notify-digest-hour">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 24 }, (_, i) => (
                  <SelectItem key={i} value={String(i)}>{`${String(i).padStart(2, '0')}:00`}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-zinc-500">毎日この時刻以降の最初のポーリングでダイジェストが配信されます</span>
          </div>
        </div>
      </SectionCard>

      {testOpen && (
        <TestSendDialog onClose={() => setTestOpen(false)} />
      )}
    </div>
  );
}


// ── UI atoms ─────────────────────────────────────────────────────────────

// ── Recipient Preview Banner ─────────────────────────────────────────────
// Sticky at the top of the settings page. Shows a live count of unique
// recipients that would receive a notification with the CURRENT (possibly
// unsaved) config. Expands to reveal the full email list + per-department
// diffs so admins can spot outliers before pressing 保存.
function RecipientPreviewBanner({ preview, loading, expanded, onToggle }) {
  // First-load / no data yet
  if (!preview) {
    return (
      <div
        className="rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-2.5 text-[12px] text-zinc-500 flex items-center gap-2"
        data-testid="notify-preview-banner-loading"
      >
        <ArrowClockwise size={14} weight="duotone" className="animate-spin" />
        受信者を計算中…
      </div>
    );
  }

  const { total, default: def, by_department: byDept, unreachable } = preview;
  // "Best-fit" tone: rose if nobody would receive anything, amber if it's
  // very small (< 2 people — likely a mistake), emerald otherwise.
  const tone = unreachable
    ? { bg: 'bg-rose-50', border: 'border-rose-300', text: 'text-rose-900', icon: 'text-rose-600', accent: 'text-rose-700' }
    : total <= 1
      ? { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-900', icon: 'text-amber-600', accent: 'text-amber-700' }
      : { bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-900', icon: 'text-emerald-600', accent: 'text-emerald-700' };

  return (
    <div
      className={`rounded-lg border-2 ${tone.border} ${tone.bg} overflow-hidden`}
      data-testid="notify-preview-banner"
    >
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-black/[0.02] ${tone.text}`}
        data-testid="notify-preview-toggle"
      >
        <span className={`inline-flex items-center justify-center w-10 h-10 rounded-lg bg-white shadow-sm ${tone.icon} shrink-0`}>
          {unreachable ? <XCircle size={20} weight="duotone" /> : <Users size={20} weight="duotone" />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-bold flex items-center gap-2">
            {unreachable ? (
              <>
                <span className="text-rose-700">誰にも通知が届きません</span>
                <Warning size={13} weight="fill" className="text-rose-600" />
              </>
            ) : (
              <>
                現在の設定で <span className={`${tone.accent} text-[20px] tabular-nums`} data-testid="notify-preview-total">{total}</span> 人が通知を受信します
                {loading && (
                  <ArrowClockwise size={12} weight="duotone" className="animate-spin text-zinc-500" />
                )}
              </>
            )}
          </div>
          <div className="text-[11px] text-zinc-600 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
            <span data-testid="notify-preview-default-count">
              既定: <b className="tabular-nums">{def.count}</b> 人（部門指定なし）
            </span>
            {byDept.length > 0 && (
              <span data-testid="notify-preview-dept-count">
                部門別上書き: <b className="tabular-nums">{byDept.length}</b> 部門
              </span>
            )}
            <span className="text-zinc-400">
              {expanded ? '折りたたむ' : '展開して詳細を見る'}
            </span>
          </div>
        </div>
        <span className={`shrink-0 ${tone.icon}`}>
          {expanded ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-zinc-200 bg-white px-4 py-3 space-y-3" data-testid="notify-preview-expanded">
          {/* Unreachable helper */}
          {unreachable && (
            <div className="text-[12px] text-rose-800">
              共通宛先が空、部門別上書きが空、そしてロール自動宛先も OFF になっています。
              少なくとも 1 つの受信者ソースを設定してください。
            </div>
          )}

          {/* Default recipients */}
          <div>
            <div className="text-xs font-semibold text-zinc-700 tracking-wide mb-1">
              既定の受信者 (部門なしイベント)
            </div>
            {def.emails.length === 0 ? (
              <div className="text-[12px] text-zinc-400 italic">なし</div>
            ) : (
              <div className="flex flex-wrap gap-1" data-testid="notify-preview-default-emails">
                {def.emails.map((e) => (
                  <span
                    key={e}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-700 text-[11px] font-mono"
                    title={e}
                  >
                    <EnvelopeSimple size={10} weight="duotone" />
                    {e}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Per-department overrides that differ from the default */}
          {byDept.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-zinc-700 tracking-wide mb-1">
                部門別上書き（既定と異なる部門のみ表示）
              </div>
              <div className="space-y-1.5" data-testid="notify-preview-dept-list">
                {byDept.map((d) => (
                  <div
                    key={d.department_id}
                    className="text-[12px] rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2"
                    data-testid={`notify-preview-dept-${d.department_id}`}
                  >
                    <div className="flex items-center gap-2 font-semibold text-zinc-900">
                      <Buildings size={12} weight="duotone" className="text-indigo-600" />
                      {d.department_name}
                      <span className="text-[10px] text-zinc-500 font-normal">
                        · <span className="tabular-nums">{d.count}</span> 人
                      </span>
                    </div>
                    {(d.added.length > 0 || d.removed.length > 0) && (
                      <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px]">
                        {d.added.map((e) => (
                          <span key={`add-${e}`} className="text-emerald-700">
                            <b>+</b> <span className="font-mono">{e}</span>
                          </span>
                        ))}
                        {d.removed.map((e) => (
                          <span key={`rm-${e}`} className="text-rose-700">
                            <b>−</b> <span className="font-mono">{e}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function SectionCard({ title, subtitle, Icon, accent, children }) {
  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden">
      <div className={`bg-gradient-to-r ${accent} px-4 py-3 flex items-center gap-2 text-white`}>
        <Icon size={18} weight="fill" />
        <div>
          <div className="text-sm font-bold">{title}</div>
          <div className="text-[11px] opacity-90">{subtitle}</div>
        </div>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}


function EventToggle({ label, desc, checked, onCheckedChange, mode, testId }) {
  return (
    <div className={`rounded-lg border p-3 transition-colors ${
      checked ? 'border-sky-200 bg-sky-50/50' : 'border-zinc-200 bg-zinc-50/60'
    }`}>
      <div className="flex items-start gap-3">
        <Switch checked={checked} onCheckedChange={onCheckedChange} data-testid={testId} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold text-zinc-800">{label}</div>
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
              mode === '即時' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-800'
            }`}>{mode}</span>
          </div>
          <p className="text-[11px] text-zinc-500 mt-1 leading-snug">{desc}</p>
        </div>
      </div>
    </div>
  );
}


function EmailListEditor({ value, onChange, testId }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const t = draft.trim();
    if (!t || !t.includes('@')) { toast.error('メール形式が不正です'); return; }
    if ((value || []).includes(t)) { setDraft(''); return; }
    onChange([...(value || []), t]); setDraft('');
  };
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {(value || []).length === 0 && (
          <span className="text-xs text-zinc-400">まだ登録されていません</span>
        )}
        {(value || []).map((e) => (
          <span key={e} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-sky-50 border border-sky-200 text-sky-800 text-[11px]"
                data-testid={`${testId}-chip-${e}`}>
            {e}
            <button
              type="button"
              onClick={() => onChange((value || []).filter((x) => x !== e))}
              className="hover:bg-sky-200 rounded p-0.5"
              title="削除"
              data-testid={`${testId}-remove-${e}`}
            >
              <Trash size={11} weight="bold" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Input
          type="email"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())}
          placeholder="example@sensbey.co.jp"
          className="h-9"
          data-testid={`${testId}-input`}
        />
        <Button variant="outline" size="sm" onClick={add} data-testid={`${testId}-add`}>
          <Plus size={12} className="mr-1" /> 追加
        </Button>
      </div>
    </div>
  );
}


function DeptOverrideEditor({ depts, value, onChange }) {
  const [pickDept, setPickDept] = useState('');
  const used = new Set((value || []).map((o) => o.department_id));
  const availableDepts = (depts || []).filter((d) => !used.has(d.id));

  const addRow = () => {
    if (!pickDept) return;
    onChange([...(value || []), { department_id: pickDept, recipients: [] }]);
    setPickDept('');
  };
  const removeRow = (dept_id) => onChange((value || []).filter((o) => o.department_id !== dept_id));
  const setRowRecipients = (dept_id, rec) =>
    onChange((value || []).map((o) => o.department_id === dept_id ? { ...o, recipients: rec } : o));

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Select value={pickDept} onValueChange={setPickDept}>
          <SelectTrigger className="h-9 flex-1" data-testid="notify-dept-picker">
            <SelectValue placeholder={availableDepts.length ? '部門を選択して追加' : '追加可能な部門がありません'} />
          </SelectTrigger>
          <SelectContent>
            {availableDepts.map((d) => (
              <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={addRow} disabled={!pickDept} data-testid="notify-dept-add">
          <Plus size={12} className="mr-1" /> 部門を追加
        </Button>
      </div>
      {(value || []).length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-zinc-200 p-6 text-center text-xs text-zinc-400">
          部門別の追加送信先はまだ設定されていません
        </div>
      ) : (
        <div className="space-y-2">
          {(value || []).map((row) => {
            const dept = depts.find((d) => d.id === row.department_id);
            return (
              <div key={row.department_id} className="rounded-lg border border-zinc-200 p-3 bg-zinc-50/40"
                data-testid={`notify-dept-row-${row.department_id}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="text-sm font-semibold text-zinc-800">
                    {dept?.name || row.department_id}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeRow(row.department_id)}
                    className="text-xs text-rose-600 hover:bg-rose-50 px-2 py-1 rounded"
                    title="この部門をリストから削除"
                    data-testid={`notify-dept-remove-${row.department_id}`}
                  >
                    <Trash size={12} className="inline mr-1" />
                    削除
                  </button>
                </div>
                <EmailListEditor
                  value={row.recipients}
                  onChange={(v) => setRowRecipients(row.department_id, v)}
                  testId={`notify-dept-${row.department_id}`}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function RoleUserPicker({ users, value, onChange }) {
  const picked = new Set(value || []);
  const anyPicked = picked.size > 0;
  const toggle = (uid) => {
    const next = new Set(picked);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    onChange(Array.from(next));
  };
  const admins = users.filter((u) => u.is_admin);
  const editors = users.filter((u) => !u.is_admin && u.is_shizai_edit);

  return (
    <div className="space-y-3">
      {/* Mode banner */}
      <div className={`rounded-lg border px-3 py-2 flex items-start gap-2 text-[12px] ${
        anyPicked
          ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
          : 'border-amber-200 bg-amber-50 text-amber-900'
      }`}>
        <ShieldCheck size={14} weight="duotone" className={`shrink-0 mt-0.5 ${anyPicked ? 'text-emerald-500' : 'text-amber-500'}`} />
        <div>
          {anyPicked ? (
            <>
              <span className="font-semibold">選択モード:</span> チェックされた {picked.size}人のみに送信されます。
              未チェックの管理者・shizai_editユーザーには送信されません (CEO等のスパム除外に有用)。
            </>
          ) : (
            <>
              <span className="font-semibold">全員モード:</span> 対象の {users.length}人 (管理者・shizai_edit) 全員に送信されます。
              誰かをチェックすると「その人のみ」モードに切り替わります。
            </>
          )}
        </div>
      </div>

      {/* Quick actions */}
      <div className="flex items-center gap-2 text-[11px]">
        <button
          type="button"
          className="px-2 py-1 rounded border border-zinc-200 hover:bg-zinc-50 text-zinc-700"
          onClick={() => onChange(users.map((u) => u.id))}
          data-testid="notify-role-select-all"
        >
          全員を選択
        </button>
        <button
          type="button"
          className="px-2 py-1 rounded border border-zinc-200 hover:bg-zinc-50 text-zinc-700"
          onClick={() => onChange([])}
          data-testid="notify-role-clear"
        >
          クリア (全員モードに戻す)
        </button>
      </div>

      {/* Admin group */}
      {admins.length > 0 && (
        <div>
          <div className="text-[10px] font-semibold uppercase text-zinc-500 tracking-wide mb-1.5 flex items-center gap-1">
            <Crown size={11} weight="fill" className="text-amber-500" />
            管理者 ({admins.length})
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {admins.map((u) => (
              <UserRow key={u.id} u={u} picked={picked.has(u.id)} onClick={() => toggle(u.id)} />
            ))}
          </div>
        </div>
      )}
      {editors.length > 0 && (
        <div>
          <div className="text-[10px] font-semibold uppercase text-zinc-500 tracking-wide mb-1.5 flex items-center gap-1">
            <UserCircle size={11} weight="fill" className="text-sky-500" />
            資材編集権限 ({editors.length})
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {editors.map((u) => (
              <UserRow key={u.id} u={u} picked={picked.has(u.id)} onClick={() => toggle(u.id)} />
            ))}
          </div>
        </div>
      )}
      {users.length === 0 && (
        <div className="rounded-lg border-2 border-dashed border-zinc-200 p-4 text-center text-xs text-zinc-400">
          管理者・shizai_edit権限のユーザーが見つかりません
        </div>
      )}
    </div>
  );
}


function UserRow({ u, picked, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`notify-role-user-${u.id}`}
      className={`flex items-center gap-2 px-2 py-2 rounded-lg border text-left transition-colors ${
        picked
          ? 'border-emerald-300 bg-emerald-50 hover:bg-emerald-100'
          : 'border-zinc-200 hover:bg-zinc-50'
      }`}
    >
      <input
        type="checkbox"
        checked={picked}
        readOnly
        className="h-4 w-4 rounded border-zinc-300 text-emerald-600 pointer-events-none shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium text-zinc-800 truncate">{u.name}</div>
        <div className="text-[10px] text-zinc-500 truncate">{u.email}</div>
      </div>
      {u.is_admin && (
        <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[9px] font-bold">
          admin
        </span>
      )}
      {u.is_shizai_edit && !u.is_admin && (
        <span className="px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 text-[9px] font-bold">
          shizai
        </span>
      )}
    </button>
  );
}


function TestSendDialog({ onClose }) {  const [kind, setKind] = useState('req_created');
  const [to, setTo] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/send-test`, { kind, to: to || undefined }).then((r) => r.data);
      toast.success(`${res.sent_to} にテスト送信しました`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'テスト送信に失敗しました');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="notify-test-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TestTube size={18} weight="duotone" className="text-sky-500" />
            テスト送信
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold text-zinc-600">テンプレート</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="h-10 mt-1" data-testid="notify-test-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="req_created">新規発注依頼メール</SelectItem>
                <SelectItem value="po_digest_preview">発注ダイジェストプレビュー</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-600">宛先 (空欄でログイン中のメール)</Label>
            <Input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="admin@sensbey.co.jp"
              className="h-10 mt-1"
              data-testid="notify-test-to"
            />
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 flex items-start gap-2 text-[11px] text-amber-900">
            <Warning size={14} weight="duotone" className="text-amber-500 shrink-0 mt-0.5" />
            <div>テスト送信はダミーデータで送信されます。実際の送信は依頼作成やダイジェスト時刻に自動で行われます。</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>キャンセル</Button>
          <Button onClick={send} disabled={sending} className="bg-teal-700 hover:bg-teal-800" data-testid="notify-test-send">
            {sending ? '送信中...' : '送信'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
